import type { AggregatedTaskState, Patch, TaskFrame } from '@/core/models/processingTask';

/**
 * Apply patches from an SSE frame onto the aggregated state.
 * - Sort by `rev` ascending to avoid misordered commits.
 * - Skip any patch already consumed (rev <= state.revision).
 * - Target semantics: "draft" sets title/category/content;
 *   "enrich" adds ai_insight / key_points / tags / content refinements;
 *   "ui" can overwrite title/content directly;
 *   "reasoning" maps to reasoning field only.
 */
export function applyFrame(state: AggregatedTaskState, frame: TaskFrame): AggregatedTaskState {
  let next: AggregatedTaskState = {
    ...state,
    status: frame.status ?? state.status,
    stage: frame.stage ?? state.stage,
    progress: frame.progress ?? state.progress,
    warning: frame.warning ?? state.warning,
    error: frame.error ?? state.error,
    note_id: frame.note_id ?? state.note_id,
  };
  if (frame.reasoning_content) next.reasoning = frame.reasoning_content;

  const patches = (frame.patches ?? []).slice().sort((a, b) => a.rev - b.rev);
  for (const p of patches) {
    if (p.rev <= next.revision) continue;
    next = applyPatch(next, p);
    next.revision = p.rev;
  }
  return next;
}

function applyPatch(state: AggregatedTaskState, p: Patch): AggregatedTaskState {
  const d = p.data ?? {};
  const next = { ...state };

  if (p.target === 'reasoning') {
    const r = pickString(d, 'reasoning') ?? pickString(d, 'content');
    if (r) next.reasoning = p.op === 'append' ? (next.reasoning ?? '') + r : r;
    return next;
  }

  const title = pickString(d, 'title');
  if (title) next.title = title;

  const category = pickString(d, 'category');
  if (category) next.category = category;

  const content = pickString(d, 'content') ?? pickString(d, 'body');
  if (content) {
    next.content = p.op === 'append' ? (next.content ?? '') + content : content;
  }

  const tags = pickArray<string>(d, 'tags');
  if (tags) next.tags = tags;

  const keyPoints = pickArray<string>(d, 'key_points');
  if (keyPoints) next.key_points = keyPoints;

  const insight = pickString(d, 'ai_insight') ?? pickString(d, 'insight');
  if (insight !== undefined) next.ai_insight = insight;

  return next;
}

function pickString(d: Record<string, unknown>, key: string): string | undefined {
  const v = d[key];
  return typeof v === 'string' ? v : undefined;
}

function pickArray<T>(d: Record<string, unknown>, key: string): T[] | undefined {
  const v = d[key];
  return Array.isArray(v) ? (v as T[]) : undefined;
}
